import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { clearErrors, getOrderDetails, updateOrder } from '../../actions/orderAction';
import { UPDATE_ORDER_RESET } from '../../constants/orderConstants';
import Loading from './Loading';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import MetaData from '../Layouts/MetaData';

const LOCKED_EDIT_STATUSES = ['Delivered', 'Cancelled'];

const allStatuses = ["Processing", "Accepted", "Packed", "Shipped", "Delivered", "Cancelled"];

const UpdateOrder = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();

    const [status, setStatus] = useState("");
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [selectedVehicleId, setSelectedVehicleId] = useState("");
    const [assigning, setAssigning] = useState(false);

    const { order, error, loading } = useSelector((state) => state.orderDetails);
    const { isUpdated, error: updateError } = useSelector((state) => state.order);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (updateError) {
            enqueueSnackbar(updateError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("Order Updated Successfully", { variant: "success" });
            dispatch({ type: UPDATE_ORDER_RESET });
        }
        dispatch(getOrderDetails(params.id));
    }, [dispatch, error, params.id, isUpdated, updateError, enqueueSnackbar]);

    useEffect(() => {
        if (order?.orderStatus) setStatus(order.orderStatus);
        if (order?.assignedDriver?._id) setSelectedDriverId(order.assignedDriver._id);
        if (order?.assignedVehicle?._id) setSelectedVehicleId(order.assignedVehicle._id);
    }, [order?.orderStatus, order?.assignedDriver, order?.assignedVehicle]);

    useEffect(() => {
        const fetchDriversAndVehicles = async () => {
            try {
                const [driversRes, vehiclesRes] = await Promise.all([
                    axios.get('/api/v1/admin/drivers'),
                    axios.get('/api/v1/admin/vehicles'),
                ]);
                setDrivers(driversRes.data.drivers);
                setVehicles(vehiclesRes.data.vehicles);
            } catch (err) {
                console.error('Failed to load drivers/vehicles', err);
            }
        };
        fetchDriversAndVehicles();
    }, []);

    const updateOrderSubmitHandler = (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.set("status", status);
        dispatch(updateOrder(params.id, formData));
    }

    const assignHandler = async () => {
        if (!selectedDriverId && !selectedVehicleId) {
            enqueueSnackbar("Please choose a driver and/or vehicle", { variant: "warning" });
            return;
        }
        setAssigning(true);
        try {
            await axios.put(`/api/v1/admin/order/${order._id}/assign-driver`, {
                driverId: selectedDriverId || undefined,
                vehicleId: selectedVehicleId || undefined,
            });
            enqueueSnackbar("Assignment updated", { variant: "success" });
            dispatch(getOrderDetails(params.id));
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to update assignment", { variant: "error" });
        } finally {
            setAssigning(false);
        }
    };

    const itemsSubtotal = order?.orderItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
    const [walletSettings, setWalletSettings] = useState(null);
    useEffect(() => {
        axios.get('/api/v1/wallet-settings').then(({ data }) => setWalletSettings(data.settings)).catch(() => {});
    }, []);

    // --- Download Invoice dialog: pick which version + how many copies ---
    const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState("");
    const [downloadCopies, setDownloadCopies] = useState(1);

    const openDownloadDialog = () => {
        const versions = order.invoiceVersions || [];
        setSelectedVersionId(versions.length > 0 ? versions[versions.length - 1]._id : "");
        setDownloadCopies(1);
        setDownloadDialogOpen(true);
    };

    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [refundAmount, setRefundAmount] = useState("");
    const [refunding, setRefunding] = useState(false);

    const [sendingTopupLink, setSendingTopupLink] = useState(false);

    const sendTopupLinkHandler = async () => {
        setSendingTopupLink(true);
        try {
            await axios.post(`/api/v1/admin/order/${order._id}/send-topup-link`);
            enqueueSnackbar("Payment link sent to customer", { variant: "success" });
            dispatch(getOrderDetails(params.id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to send payment link", { variant: "error" });
        } finally {
            setSendingTopupLink(false);
        }
    };

    const openRefundDialog = () => {
        setRefundAmount(order.refundDue > 0 ? order.refundDue.toString() : "");
        setRefundDialogOpen(true);
    };

    const confirmRefund = async () => {
        const amount = Number(refundAmount);
        if (!amount || amount <= 0) {
            enqueueSnackbar("Please enter a valid refund amount", { variant: "error" });
            return;
        }
        setRefunding(true);
        try {
            await axios.post(`/api/v1/admin/order/${order._id}/refund`, { amount });
            enqueueSnackbar("Refund issued successfully", { variant: "success" });
            setRefundDialogOpen(false);
            dispatch(getOrderDetails(params.id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Refund failed", { variant: "error" });
        } finally {
            setRefunding(false);
        }
    };

    const confirmDownload = async () => {
        const copies = Math.max(1, Math.min(parseInt(downloadCopies, 10) || 1, 50));
        const versionIndex = (order.invoiceVersions || []).findIndex((v) => v._id === selectedVersionId);

        const url = selectedVersionId
            ? `/api/v1/admin/order/${order._id}/invoice/${selectedVersionId}?copies=${copies}`
            : `/api/v1/admin/order/${order._id}/invoice?copies=${copies}`;
        const filenameSuffix = versionIndex >= 0 ? `-v${versionIndex + 1}` : '';

        try {
            const { data } = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `invoice-${order.orderNumber ?? order._id}${filenameSuffix}${copies > 1 ? `-x${copies}` : ''}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            setDownloadDialogOpen(false);
        } catch (error) {
            enqueueSnackbar("Failed to download invoice", { variant: "error" });
        }
    };

    const downloadInvoiceVersionHandler = async (versionId, versionIndex) => {
        try {
            const { data } = await axios.get(`/api/v1/admin/order/${order._id}/invoice/${versionId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${order.orderNumber ?? order._id}-v${versionIndex + 1}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            enqueueSnackbar("Failed to download this invoice version", { variant: "error" });
        }
    };

    // --- Edit Order panel ---
    const [editing, setEditing] = useState(false);
    const [editItems, setEditItems] = useState([]);
    const [savingEdit, setSavingEdit] = useState(false);
    const [productSearch, setProductSearch] = useState("");
    const [productOptions, setProductOptions] = useState([]);

    // 'auto' | 'waive' | 'charge'
    const [shippingOverride, setShippingOverride] = useState('auto');

    const startEditing = () => {
        setEditItems(order.orderItems.map((i) => ({
            product: i.product,
            name: i.name,
            price: i.price,
            image: i.image,
            quantity: i.quantity,
        })));
        setShippingOverride('auto');
        setEditing(true);
    };

    useEffect(() => {
        if (!productSearch) { setProductOptions([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/v1/products', { params: { keyword: productSearch } });
                setProductOptions(data.products || []);
            } catch (error) { /* ignore */ }
        }, 400);
        return () => clearTimeout(timeout);
    }, [productSearch]);

    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (!editing || editItems.length === 0) { setPreview(null); return; }
        setPreviewLoading(true);
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.post(`/api/v1/admin/order/${order._id}/preview-edit`, {
                    orderItems: editItems.map((i) => ({ product: i.product, quantity: i.quantity })),
                });
                setPreview(data);
            } catch (error) {
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 400);
        return () => clearTimeout(timeout);
    }, [editing, editItems, order?._id]);

    // Only meaningful when this edit actually flips the shipping charge
    // between free and charged — otherwise there's nothing to override.
    const shippingStatusChanging = preview && ((order.shippingCharge > 0) !== (preview.shippingCharge > 0));

    // For display only: shows what the total would be under each toggle
    // choice, using the order's existing shipping fee as the reapply
    // amount (the actual save re-derives this from the shipping area).
    const displayShipping = !shippingStatusChanging
        ? preview?.shippingCharge ?? 0
        : shippingOverride === 'waive' ? 0
        : shippingOverride === 'charge' ? (order.shippingCharge || preview?.shippingCharge || 0)
        : preview?.shippingCharge ?? 0;
    const displayTotal = preview ? Number((preview.totalPrice - preview.shippingCharge + displayShipping).toFixed(2)) : null;

    const updateEditQty = (product, delta) => {
        setEditItems((prev) => prev.map((i) => i.product === product ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter((i) => i.quantity > 0));
    };

    const addEditProduct = (product) => {
        if (!product) return;
        setEditItems((prev) => {
            const existing = prev.find((i) => i.product === product._id);
            if (existing) {
                return prev.map((i) => i.product === product._id ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, { product: product._id, name: product.name, price: product.price, image: product.images?.[0]?.url, quantity: 1 }];
        });
    };

    const saveOrderEdit = async () => {
        if (editItems.length === 0) {
            enqueueSnackbar("Order must have at least one item", { variant: "error" });
            return;
        }
        setSavingEdit(true);
        try {
            await axios.put(`/api/v1/admin/order/${order._id}/edit`, {
                orderItems: editItems.map((i) => ({ product: i.product, quantity: i.quantity })),
                shippingOverride,
            });
            enqueueSnackbar("Order updated", { variant: "success" });
            setEditing(false);
            dispatch(getOrderDetails(params.id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update order", { variant: "error" });
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <>
            <MetaData title="Admin: Update Order | 1313 Mart" />

            {loading ? <Loading /> : (
                <>
                    {order && order.user && order.shippingInfo && (
                        <div className="flex flex-col gap-4">

                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <Link to="/admin/orders" className="ml-1 flex items-center gap-0 font-medium text-primary-blue uppercase"><ArrowBackIosIcon sx={{ fontSize: "18px" }} />Go Back</Link>
                                    <p className="text-sm text-gray-500">Order #{order.orderNumber ?? order._id}</p>
                                </div>
                                <div className="flex gap-2">
                                    {!editing && !LOCKED_EDIT_STATUSES.includes(order.orderStatus) && (
                                        <button
                                            onClick={startEditing}
                                            className="flex items-center gap-1.5 bg-primary-orange text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg"
                                        >
                                            <EditIcon sx={{ fontSize: "18px" }} /> Edit Order
                                        </button>
                                    )}
                                    <button
                                        onClick={openDownloadDialog}
                                        className="flex items-center gap-1.5 bg-primary-blue text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg"
                                    >
                                        <DownloadIcon sx={{ fontSize: "18px" }} /> Download Invoice
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row bg-white shadow-lg rounded-lg min-w-full">
                                <div className="sm:w-1/2 border-r">
                                    <div className="flex flex-col gap-3 my-8 mx-10">
                                        <h3 className="font-medium text-lg">Delivery Address</h3>
                                        <h4 className="font-medium">{order.user.name}</h4>
                                        <p className="text-sm">{`${order.shippingInfo.address}, ${order.shippingInfo.city}, ${order.shippingInfo.state}`}</p>
                                        <div className="flex gap-2 text-sm">
                                            <p className="font-medium">Email</p>
                                            <p>{order.user.email}</p>
                                        </div>
                                        <div className="flex gap-2 text-sm">
                                            <p className="font-medium">Phone Number</p>
                                            <p>{order.shippingInfo.phoneNo}</p>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={updateOrderSubmitHandler} className="flex flex-col gap-3 p-8">
                                    <h3 className="font-medium text-lg">Update Status</h3>
                                    <div className="flex gap-2">
                                        <p className="text-sm font-medium">Current Status:</p>
                                        <p className="text-sm">{order.orderStatus}</p>
                                    </div>
                                    <FormControl fullWidth sx={{ marginTop: 1 }}>
                                        <InputLabel id="order-status-select-label">Status</InputLabel>
                                        <Select
                                            labelId="order-status-select-label"
                                            id="order-status-select"
                                            value={status}
                                            label="Status"
                                            onChange={(e) => setStatus(e.target.value)}
                                        >
                                            {allStatuses.map((s) => (
                                                <MenuItem value={s} key={s}>{s}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <p className="text-xs text-gray-500">You can set the status to any value at any time — nothing is locked, even after Delivered or Cancelled.</p>
                                    <button type="submit" className="bg-primary-orange p-2.5 text-white font-medium rounded shadow hover:shadow-lg">
                                        Update
                                    </button>
                                </form>
                            </div>

                            <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-3">
                                <h3 className="font-medium text-lg">Payment Details</h3>
                                <div className="flex gap-2 text-sm">
                                    <p className="font-medium">Payment Mode</p>
                                    <p>{order.paymentMethod === 'card' ? 'Stripe (Credit Card)' : 'Cash on Delivery'}</p>
                                </div>
                                {order.paymentMethod === 'card' && (
                                    <>
                                        <div className="flex gap-2 text-sm">
                                            <p className="font-medium">Transaction ID</p>
                                            <p className="font-mono">{order.paymentInfo?.id}</p>
                                        </div>
                                        <div className="flex gap-2 text-sm">
                                            <p className="font-medium">Transaction Status</p>
                                            <p className="capitalize">{order.paymentInfo?.status}</p>
                                        </div>
                                        <div className="flex gap-2 text-sm">
                                            <p className="font-medium">Paid At</p>
                                            <p>{order.paidAt ? new Date(order.paidAt).toLocaleString() : "—"}</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-3">
                                <h3 className="font-medium text-lg">Order Logs</h3>
                                <div className="flex flex-col gap-3 max-h-72 overflow-y-auto pr-1">
                                    {order.logs && order.logs.length > 0 ? (
                                        [...order.logs].reverse().map((log, i) => (
                                            <div key={i} className="border-l-2 border-primary-blue pl-3 py-0.5">
                                                <p className="text-sm text-gray-800">{log.message}</p>
                                                <p className="text-xs text-gray-400">
                                                    {log.actor} · {new Date(log.createdAt).toLocaleString('en-HK')}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-400">No log entries yet.</p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-4">
                                <h3 className="font-medium text-lg">Delivery Assignment</h3>

                                <div className="flex flex-col gap-1">
                                    <p className="text-sm text-gray-600">
                                        Driver: {order.assignedDriver
                                            ? <span className="font-medium text-gray-800">{order.assignedDriver.name}{order.assignedDriver.phoneNo && ` · ${order.assignedDriver.phoneNo}`}</span>
                                            : <span className="text-gray-400">Not assigned</span>}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Vehicle: {order.assignedVehicle
                                            ? <span className="font-medium text-gray-800">{order.assignedVehicle.vehicleNumber}{order.assignedVehicle.type && ` (${order.assignedVehicle.type})`}</span>
                                            : <span className="text-gray-400">Not assigned</span>}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel id="driver-select-label">Choose Driver</InputLabel>
                                        <Select
                                            labelId="driver-select-label"
                                            value={selectedDriverId}
                                            label="Choose Driver"
                                            onChange={(e) => setSelectedDriverId(e.target.value)}
                                        >
                                            {drivers.map((d) => (
                                                <MenuItem value={d._id} key={d._id}>
                                                    {d.name} {!d.isAvailable && "(Unavailable)"}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel id="vehicle-select-label">Choose Vehicle</InputLabel>
                                        <Select
                                            labelId="vehicle-select-label"
                                            value={selectedVehicleId}
                                            label="Choose Vehicle"
                                            onChange={(e) => setSelectedVehicleId(e.target.value)}
                                        >
                                            {vehicles.map((v) => (
                                                <MenuItem value={v._id} key={v._id}>
                                                    {v.vehicleNumber} {!v.active && "(Inactive)"}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <button
                                        onClick={assignHandler}
                                        disabled={assigning}
                                        className="bg-primary-blue text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg disabled:opacity-50"
                                    >
                                        Save Assignment
                                    </button>
                                </div>
                            </div>

                            {editing ? (
                                <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-4">
                                    <h3 className="font-medium text-lg">Editing Order Items</h3>

                                    {editItems.map((item) => (
                                        <div key={item.product} className="flex items-center gap-3 border-b pb-3">
                                            <img draggable="false" className="w-10 h-10 object-contain flex-shrink-0" src={item.image} alt={item.name} />
                                            <span className="flex-1 text-sm">{item.name}</span>
                                            <span className="text-sm text-gray-500">HK${item.price.toLocaleString()}</span>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => updateEditQty(item.product, -1)} className="w-7 h-7 bg-gray-50 rounded-full border flex items-center justify-center"><RemoveIcon sx={{ fontSize: "16px" }} /></button>
                                                <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                                                <button onClick={() => updateEditQty(item.product, 1)} className="w-7 h-7 bg-gray-50 rounded-full border flex items-center justify-center"><AddIcon sx={{ fontSize: "16px" }} /></button>
                                            </div>
                                            <span className="text-sm font-medium w-20 text-right">HK${(item.price * item.quantity).toLocaleString()}</span>
                                            <button onClick={() => updateEditQty(item.product, -item.quantity)} className="text-red-500"><DeleteIcon sx={{ fontSize: "18px" }} /></button>
                                        </div>
                                    ))}

                                    <Autocomplete
                                        size="small"
                                        options={productOptions}
                                        getOptionLabel={(opt) => opt.name || ""}
                                        onChange={(e, newValue) => addEditProduct(newValue)}
                                        onInputChange={(e, newInput) => setProductSearch(newInput)}
                                        value={null}
                                        renderInput={(params) => <TextField {...params} label="Add another product" placeholder="Search products..." />}
                                    />

                                    {shippingStatusChanging && (
                                        <div className="bg-orange-50 border border-orange-200 rounded p-3">
                                            <p className="text-xs font-medium text-gray-700 mb-1">
                                                This change {preview.shippingCharge > 0 ? 'now requires' : 'now qualifies for free'} shipping — choose what to do:
                                            </p>
                                            <RadioGroup row value={shippingOverride} onChange={(e) => setShippingOverride(e.target.value)}>
                                                <FormControlLabel value="auto" control={<Radio size="small" />} label="Auto (recommended)" />
                                                <FormControlLabel value="waive" control={<Radio size="small" />} label="Waive shipping" />
                                                <FormControlLabel value="charge" control={<Radio size="small" />} label="Charge shipping" />
                                            </RadioGroup>
                                        </div>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <div className="flex-1 bg-gray-50 rounded p-3 flex flex-col gap-1 text-sm">
                                            <p className="font-medium text-gray-500 text-xs uppercase mb-1">Original (before edit)</p>
                                            <div className="flex justify-between"><span>Subtotal</span><span>HK${(order.subtotal ?? itemsSubtotal).toLocaleString()}</span></div>
                                            <div className="flex justify-between"><span>Shipping</span><span>{order.shippingCharge > 0 ? `HK$${order.shippingCharge.toLocaleString()}` : 'FREE'}</span></div>
                                            <div className="flex justify-between font-medium border-t pt-1 mt-1"><span>Total</span><span>HK${order.totalPrice.toLocaleString()}</span></div>
                                        </div>
                                        <div className="flex-1 bg-orange-50 rounded p-3 flex flex-col gap-1 text-sm">
                                            <p className="font-medium text-orange-600 text-xs uppercase mb-1">Live (after this edit)</p>
                                            {previewLoading || !preview ? (
                                                <p className="text-gray-400 text-xs">Calculating...</p>
                                            ) : (
                                                <>
                                                    <div className="flex justify-between"><span>Subtotal</span><span>HK${preview.subtotal.toLocaleString()}</span></div>
                                                    <div className="flex justify-between"><span>Shipping</span><span>{displayShipping > 0 ? `HK$${displayShipping.toLocaleString()}` : 'FREE'}</span></div>
                                                    <div className="flex justify-between font-medium border-t pt-1 mt-1"><span>Total</span><span>HK${displayTotal.toLocaleString()}</span></div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <p className="text-xs text-gray-500">
                                        If the new total is lower and this order was paid online, the difference will be recorded as a refund due. If it's higher and the order was paid online, a payment link for just the difference will be ready to send to the customer once you save.
                                    </p>

                                    <div className="flex justify-end gap-3">
                                        <button onClick={() => setEditing(false)} className="py-2.5 px-6 text-gray-600 text-sm font-medium">Cancel</button>
                                        <button
                                            onClick={saveOrderEdit}
                                            disabled={savingEdit}
                                            className="bg-primary-orange py-2.5 px-8 text-white text-sm font-medium shadow hover:shadow-lg rounded disabled:opacity-60"
                                        >
                                            {savingEdit ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                                            <tr>
                                                <th className="text-left px-4 py-3">Item</th>
                                                <th className="text-right px-4 py-3">Qty</th>
                                                <th className="text-right px-4 py-3">Price</th>
                                                <th className="text-right px-4 py-3">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.orderItems.map((item) => (
                                                <tr key={item._id} className="border-t">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <img draggable="false" className="w-10 h-10 object-contain flex-shrink-0" src={item.image} alt={item.name} />
                                                            <span>{item.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-right px-4 py-3">{item.quantity}</td>
                                                    <td className="text-right px-4 py-3">HK${item.price.toLocaleString()}</td>
                                                    <td className="text-right px-4 py-3 font-medium">HK${(item.price * item.quantity).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="border-t bg-gray-50 p-4 flex flex-col gap-1 items-end text-sm">
                                        <p className="flex justify-between w-full sm:w-64"><span>MRP Subtotal</span> <span>HK${(order.subtotal ?? itemsSubtotal).toLocaleString()}</span></p>
                                        {order.offerDiscount > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>Offer Discount</span> <span>- HK${order.offerDiscount.toLocaleString()}</span></p>
                                        )}
                                        {order.promoDiscount > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>Promo ({order.promoCode})</span> <span>- HK${order.promoDiscount.toLocaleString()}</span></p>
                                        )}
                                        <p className="flex justify-between w-full sm:w-64"><span>Shipping Charge</span> <span>{order.shippingCharge > 0 ? `HK$${order.shippingCharge.toLocaleString()}` : "FREE"}</span></p>
                                        {order.pointsRedeemed > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-orange"><span>1313 Points Used ({order.pointsRedeemed.toLocaleString()} pts)</span> <span>- HK${walletSettings ? (order.pointsRedeemed / walletSettings.pointsToCurrencyRedemption).toLocaleString() : ''}</span></p>
                                        )}
                                        {order.walletCashUsed > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>1313 Cash Used</span> <span>- HK${order.walletCashUsed.toLocaleString()}</span></p>
                                        )}
                                        <p className="flex justify-between w-full sm:w-64 font-medium text-base border-t pt-1 mt-1"><span>Total Amount</span> <span>HK${order.totalPrice.toLocaleString()}</span></p>
                                        {order.pointsEarned > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-xs text-gray-400"><span>Points earned</span> <span>{order.pointsEarned.toLocaleString()}</span></p>
                                        )}
                                        {order.refundDue > 0 && (
                                            <div className="flex justify-between items-center w-full sm:w-64">
                                                <p className="text-red-600 font-medium">Refund Due: HK${order.refundDue.toLocaleString()}</p>
                                                {order.paymentMethod && order.paymentMethod !== 'cod' && (
                                                    <button
                                                        onClick={openRefundDialog}
                                                        className="text-xs bg-red-600 text-white font-medium px-3 py-1.5 rounded shadow hover:shadow-lg"
                                                    >
                                                        Refund
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {order.additionalPaymentDue > 0 && (
                                            <div className="flex justify-between items-center w-full sm:w-64">
                                                <p className="text-primary-orange font-medium">Additional Payment Due: HK${order.additionalPaymentDue.toLocaleString()}</p>
                                                <button
                                                    onClick={sendTopupLinkHandler}
                                                    disabled={sendingTopupLink}
                                                    className="text-xs bg-primary-orange text-white font-medium px-3 py-1.5 rounded shadow hover:shadow-lg disabled:opacity-60"
                                                >
                                                    {sendingTopupLink ? "Sending..." : (order.additionalPaymentUrl ? "Resend Link" : "Send Payment Link")}
                                                </button>
                                            </div>
                                        )}
                                        {order.additionalPaymentUrl && (
                                            <p className="text-xs text-gray-400 w-full sm:w-64 break-all">
                                                Link: <a href={order.additionalPaymentUrl} target="_blank" rel="noreferrer" className="text-primary-blue hover:underline">{order.additionalPaymentUrl}</a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {order.invoiceVersions && order.invoiceVersions.length > 0 && (
                                <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-3">
                                    <h3 className="font-medium text-lg">Invoice History</h3>
                                    <div className="flex flex-col divide-y">
                                        {order.invoiceVersions.map((v, i) => (
                                            <div key={v._id || i} className="flex justify-between items-center py-2.5">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800">Version {i + 1} — {v.note}</p>
                                                    <p className="text-xs text-gray-400">HK${v.totalPrice.toLocaleString()} · {new Date(v.createdAt).toLocaleString('en-HK')}</p>
                                                </div>
                                                <button
                                                    onClick={() => downloadInvoiceVersionHandler(v._id, i)}
                                                    className="flex items-center gap-1 text-sm text-primary-blue font-medium"
                                                >
                                                    <DownloadIcon sx={{ fontSize: "16px" }} /> Download
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    )}
                </>
            )}

            <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Download Invoice</DialogTitle>
                <DialogContent className="flex flex-col gap-4">
                    <div>
                        <p className="text-sm font-medium mb-2">Which version?</p>
                        <RadioGroup value={selectedVersionId} onChange={(e) => setSelectedVersionId(e.target.value)}>
                            {(order?.invoiceVersions || []).map((v, i) => (
                                <FormControlLabel
                                    key={v._id}
                                    value={v._id}
                                    control={<Radio size="small" />}
                                    label={`Version ${i + 1} — ${v.note} (HK$${v.totalPrice.toLocaleString()}, ${new Date(v.createdAt).toLocaleDateString('en-HK')})`}
                                />
                            ))}
                            {(!order?.invoiceVersions || order.invoiceVersions.length === 0) && (
                                <FormControlLabel value="" control={<Radio size="small" />} label="Current order (no version history)" checked />
                            )}
                        </RadioGroup>
                    </div>
                    <TextField
                        label="Number of copies"
                        type="number"
                        size="small"
                        inputProps={{ min: 1, max: 50 }}
                        value={downloadCopies}
                        onChange={(e) => setDownloadCopies(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setDownloadDialogOpen(false)} className="py-2 px-4 text-sm text-gray-600">Cancel</button>
                    <button onClick={confirmDownload} className="py-2 px-5 bg-primary-blue text-white text-sm font-medium rounded shadow hover:shadow-lg">Download</button>
                </DialogActions>
            </Dialog>

            <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Issue Refund</DialogTitle>
                <DialogContent className="flex flex-col gap-3">
                    <p className="text-sm text-gray-600">
                        This refunds the customer's original payment through Stripe directly — no need to leave this page.
                    </p>
                    <TextField
                        label="Refund amount (HK$)"
                        type="number"
                        size="small"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        autoFocus
                    />
                    <p className="text-xs text-gray-400">Pre-filled with the tracked refund-due amount — adjust if needed.</p>
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setRefundDialogOpen(false)} className="py-2 px-4 text-sm text-gray-600">Cancel</button>
                    <button onClick={confirmRefund} disabled={refunding} className="py-2 px-5 bg-red-600 text-white text-sm font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        {refunding ? "Processing..." : "Issue Refund"}
                    </button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdateOrder;